/**
 * Debug Logger - Advanced debugging tool for React applications
 * Provides detailed logging of component interactions, events, and data
 */
export default function applyDebugLogger() {
  window.DEBUG = {
    enabled: true,
    log: function(module, message, data) {
      if (!this.enabled) return;
      
      const timestamp = new Date().toISOString();
      console.log(`[${timestamp}] [${module}] ${message}`, data || '');
      
      // Also save to session storage for persistence
      try {
        const logs = JSON.parse(sessionStorage.getItem('debug_logs') || '[]');
        logs.push({
          timestamp,
          module,
          message,
          data: data ? JSON.stringify(data).substring(0, 500) : null
        });
        // Keep only the last 100 logs
        if (logs.length > 100) logs.shift();
        sessionStorage.setItem('debug_logs', JSON.stringify(logs));
      } catch (e) {
        // Ignore storage errors
      }
    },
    
    error: function(module, message, error) {
      if (!this.enabled) return;
      
      const timestamp = new Date().toISOString();
      console.error(`[${timestamp}] [${module}] ERROR: ${message}`, error);
      
      // Also save to session storage for persistence
      try {
        const logs = JSON.parse(sessionStorage.getItem('debug_errors') || '[]');
        logs.push({
          timestamp,
          module,
          message,
          stack: error && error.stack ? error.stack : null
        });
        // Keep only the last 50 errors
        if (logs.length > 50) logs.shift();
        sessionStorage.setItem('debug_errors', JSON.stringify(logs));
      } catch (e) {
        // Ignore storage errors
      }
    },
    
    // Add a global error handler
    setupGlobalErrorHandler: function() {
      window.onerror = (message, source, lineno, colno, error) => {
        this.error('GlobalError', `${message} at ${source}:${lineno}:${colno}`, error);
        return false; // Don't prevent default error handling
      };
      
      window.addEventListener('unhandledrejection', (event) => {
        this.error('UnhandledPromiseRejection', 'Unhandled Promise Rejection', event.reason);
      });
    },
    
    // Track DOM events on specific elements
    trackEvents: function(selector, events) {
      const elements = document.querySelectorAll(selector);
      events = events || ['click', 'focus', 'blur', 'change'];
      
      elements.forEach(element => {
        events.forEach(eventName => {
          element.addEventListener(eventName, (e) => {
            const elementInfo = {
              id: element.id,
              className: element.className,
              tagName: element.tagName,
              value: element.value,
              type: element.type
            };
            
            this.log('DOMEvent', `${eventName} on ${selector}`, elementInfo);
          });
        });
      });
    },
    
    // Inspect React component props and state
    inspectReact: function() {
      if (!window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
        console.warn('React DevTools hook not found. React inspection not available.');
        return;
      }
      
      const reactInstances = [];
      
      // Find all React component instances
      if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__.renderers) {
        Object.values(window.__REACT_DEVTOOLS_GLOBAL_HOOK__.renderers).forEach(renderer => {
          const getFiberRoots = renderer.findFiberRoots || renderer.getFiberRoots;
          if (getFiberRoots) {
            getFiberRoots(window).forEach(root => {
              if (root.current) {
                this._walkFiber(root.current, reactInstances);
              }
            });
          }
        });
      }
      
      this.log('ReactInspect', `Found ${reactInstances.length} React components`);
      return reactInstances;
    },
    
    _walkFiber: function(fiber, instances) {
      if (!fiber) return;
      
      // Check if this is a component
      if (fiber.stateNode && fiber.type && typeof fiber.type === 'function') {
        instances.push({
          name: fiber.type.name || 'AnonymousComponent',
          props: fiber.memoizedProps,
          state: fiber.memoizedState,
          fiber: fiber
        });
      }
      
      // Walk children
      if (fiber.child) this._walkFiber(fiber.child, instances);
      if (fiber.sibling) this._walkFiber(fiber.sibling, instances);
    },
    
    // Add a floating debug panel to the page
    addDebugPanel: function() {
      // Create debug panel element
      const panel = document.createElement('div');
      panel.id = 'debug-panel';
      panel.className = 'hidden-print';
      panel.style.cssText = `
        position: fixed;
        bottom: 10px;
        right: 10px;
        width: 50px;
        height: 50px;
        background-color: rgba(0, 0, 0, 0.7);
        color: white;
        border-radius: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        cursor: pointer;
        z-index: 9999;
        transition: all 0.3s ease;
      `;
      panel.innerHTML = '🐞';
      
      panel.onclick = function() {
        if (panel.style.width === '50px') {
          // Expand panel
          panel.style.width = '80%';
          panel.style.height = '60%';
          panel.style.overflow = 'auto';
          
          // Show debug info
          const logs = JSON.parse(sessionStorage.getItem('debug_logs') || '[]');
          const errors = JSON.parse(sessionStorage.getItem('debug_errors') || '[]');
          
          panel.innerHTML = `
            <div style="padding: 10px;">
              <h2>Debug Information</h2>
              <button id="debug-close" style="position: absolute; top: 10px; right: 10px;">Close</button>
              <button id="debug-clear" style="position: absolute; top: 10px; right: 80px;">Clear Logs</button>
              
              <h3>Last 10 Errors</h3>
              <div style="max-height: 200px; overflow: auto; background: #333; padding: 5px;">
                ${errors.slice(-10).reverse().map(err => 
                  `<div style="margin-bottom: 5px; border-bottom: 1px solid #555;">
                    <strong>${err.timestamp}</strong> [${err.module}] ${err.message}
                    ${err.stack ? `<pre style="color: #f88;">${err.stack}</pre>` : ''}
                  </div>`
                ).join('')}
              </div>
              
              <h3>Recent Activity Logs</h3>
              <div style="max-height: 300px; overflow: auto; background: #333; padding: 5px;">
                ${logs.slice(-30).reverse().map(log => 
                  `<div style="margin-bottom: 5px; border-bottom: 1px solid #555;">
                    <strong>${log.timestamp}</strong> [${log.module}] ${log.message}
                    ${log.data ? `<pre>${log.data}</pre>` : ''}
                  </div>`
                ).join('')}
              </div>
            </div>
          `;
          
          document.getElementById('debug-close').onclick = function(e) {
            e.stopPropagation();
            panel.style.width = '50px';
            panel.style.height = '50px';
            panel.innerHTML = '🐞';
          };
          
          document.getElementById('debug-clear').onclick = function(e) {
            e.stopPropagation();
            sessionStorage.removeItem('debug_logs');
            sessionStorage.removeItem('debug_errors');
            panel.click(); // Refresh panel
            panel.click(); // And expand again
          };
        }
      };
      
      document.body.appendChild(panel);
    }
  };
  
  // Initialize debugging
  window.DEBUG.setupGlobalErrorHandler();
  
  // Wait for DOM to be ready
  // In React app, standard DOMContentLoaded might have already fired, so check readyState
  function init() {
    window.DEBUG.log('Debugger', 'Debug logger initialized');
    
    // Add the debug panel
    setTimeout(() => {
      window.DEBUG.addDebugPanel();
    }, 1000);
    
    // Start tracking relevant elements once they're loaded
    setTimeout(() => {
      // Look for forms, select inputs, and typeahead fields
      window.DEBUG.trackEvents('form', ['submit']);
      window.DEBUG.trackEvents('select, input[type="text"], .typeahead', ['change', 'focus', 'blur']);
      
      // Try to find React Typeahead specific elements
      window.DEBUG.trackEvents('.rbt-input-main, .rbt-menu, .dropdown-item', ['click', 'focus', 'mouseenter']);
    }, 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  console.log('Debug Logger installed');
}



// WEBPACK FOOTER //
// ./src/utils/patches/debugLogger.js